import { h, Component } from 'preact';
import { withContext } from '../../common/context';
import Placeholder from '../Placeholder/Placeholder';
import { FlexCenterStretched } from '../../utils/emotion';
import { MediaSymbolTypes } from '../../typing/enums';
import Error from '../Error/Error';
import { THREE_DRACO_URL } from '../../typing/env';
import { ThreeWrapper } from './Three.styled';
class Three extends Component {
  state = {
    error: false,
    ready: false
  };

  shouldComponentUpdate(nextProps) {
    if (nextProps.active && !this.loader && this.isScriptLoaded(nextProps)) {
      this.init(this.props.width, this.props.height);
    }

    if (nextProps.active && this.loader) {
      this.animate();
    } else if (!nextProps.active && this.loader) {
      this.stopAnimate();
    }
    if (nextProps.width !== this.props.width && this.loader) {
      this.renderer.setSize(nextProps.width, nextProps.height);
    }

    return false;
  }

  componentWillUnmount() {
    this.stopAnimate();
  }

  init(width, height) {
    if (!window.THREE) {
      return;
    }

    this.loader = new window.THREE.GLTFLoader();

    const dracoLoader = new window.THREE.DRACOLoader();
    dracoLoader.setDecoderPath(THREE_DRACO_URL);
    this.loader.setDRACOLoader(dracoLoader);

    const camera = (this.camera = new window.THREE.PerspectiveCamera(45, width / height, 10, 100));
    const controls = (this.controls = new window.THREE.OrbitControls(camera, this.wrap));
    controls.update();
    const scene = (this.scene = new window.THREE.Scene());
    scene.background = new window.THREE.Color(this.props.context.config.selectBgColor());
    const renderer = (this.renderer = new window.THREE.WebGLRenderer({
      antialias: true
    }));
    if (this.props.ariaLabel) {
      renderer.domElement.setAttribute('aria-label', this.props.ariaLabel);
    }
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(width, height);
    renderer.gammaOutput = true;
    renderer.shadowMap.enabled = true;

    renderer.shadowMap.type = window.THREE.PCFSoftShadowMap;
    this.wrap.appendChild(renderer.domElement);
    const light = (this.light = new window.THREE.HemisphereLight(0xbbbbff, 0x444422));
    light.position.set(0, 1, 0);
    scene.add(light);
    this.loadInto();
  }

  getThreeUrl() {
    return this.props.context.cloudinary.getThreeUrl(
      this.props.publicId,
      this.props.transformation
    );
  }

  loadInto() {
    const url = this.getThreeUrl();
    this.loader.load(
      url,
      gltf => {
        const { light, camera, scene, controls } = this;
        scene.add(gltf.scene);
        const boundingBox = new window.THREE.Box3();
        boundingBox.setFromObject(gltf.scene);
        const center = new window.THREE.Vector3();
        boundingBox.getCenter(center);
        const size = new window.THREE.Vector3();
        boundingBox.getSize(size);
        const maxDim = Math.max(size.x, size.y, size.z);
        const fov = camera.fov * (Math.PI / 180);
        let cameraZ = Math.abs(maxDim / Math.tan(fov / 2.0));
        camera.position.z = center.z + cameraZ;
        camera.position.x = center.x;
        camera.position.y = center.y;
        light.position.set(camera.position.x, camera.position.y, camera.position.z);
        camera.near = cameraZ / 9;
        camera.far = 10000;
        camera.lookAt(center);
        camera.updateProjectionMatrix();
        this.addLightAndBox(scene, gltf.scene, camera);
        if (controls) {
          controls.target = center;
          controls.maxDistance = cameraZ * 3;
          controls.minDistance = cameraZ / 3;
          controls.saveState();
          controls.update();
        }
        camera.updateProjectionMatrix();
      },
      // onProgress
      xhr => {
        const total = (xhr.loaded / xhr.total) * 100;
        if (total >= 100 || total === Infinity) {
          this.setState(() => ({ ready: true }));
          this.forceUpdate();
        }
      },
      // onError
      e => {
        console.error(e);
        this.setState(() => ({ error: true }));
        this.forceUpdate();
      }
    );
  }

  isScriptLoaded(props = this.props) {
    const { scriptLoader } = props.context;

    return scriptLoader?.success?.three;
  }

  stopAnimate() {
    if (this.requestID) {
      cancelAnimationFrame(this.requestID);
      this.requestID = undefined;
    }
  }

  getMetrics(object) {
    let boundingBox = new window.THREE.Box3();
    boundingBox.setFromObject(object);
    let center = new window.THREE.Vector3();
    boundingBox.getCenter(center);
    let size = new window.THREE.Vector3();
    boundingBox.getSize(size);
    let maxDim = Math.max(size.x, size.y, size.z);

    return { maxDim, boundingBox, center };
  }

  addLightAndBox(scene, object, camera) {
    const metrics = this.getMetrics(object);
    const boxSize = Math.max(2 * (camera.position.z - metrics.center.z), metrics.maxDim * 2);
    for (let i = 0; i < 2; i++) {
      const light = new window.THREE.SpotLight(0xffffff, 0.5);
      let mult = i == 0 ? -1 : 1;
      light.position.set(
        metrics.center.x + (mult * boxSize) / 3,
        metrics.center.y + boxSize / 2,
        metrics.center.z + (mult * boxSize) / 3
      );
      light.target = object;
      light.shadow.mapSize.width = 512;
      light.shadow.mapSize.height = 512;
      light.castShadow = true;
      light.shadow.camera.near = 0.01;
      scene.add(light);
    }
  }

  getBackgroundMap() {
    const url = 'https://res.cloudinary.com/demo/image/upload/v1547475875/3d/envmap-indoor.jpg';

    let textureLoader = new window.THREE.TextureLoader();
    const textureEquirec = textureLoader.load(url);

    textureEquirec.mapping = window.THREE.EquirectangularReflectionMapping;

    textureEquirec.magFilter = window.THREE.LinearFilter;

    textureEquirec.minFilter = window.THREE.LinearMipMapLinearFilter;

    textureEquirec.encoding = window.THREE.sRGBEncoding;

    return textureEquirec;
  }

  animate = () => {
    this.requestID = requestAnimationFrame(this.animate);
    this.renderer.render(this.scene, this.camera);
  };

  stopPropagation = event => {
    event.stopPropagation();
  };

  render(props) {
    const { scriptLoader } = this.props.context;
    const failLoading = !scriptLoader.isLoading && !this.isScriptLoaded();

    return (
      <FlexCenterStretched relative>
        <ThreeWrapper
          innerRef={elem => (this.wrap = elem)}
          data-test="three-wrap"
          width={props.width}
          height={props.height}
        />
        {((!this.state.error && !this.state.ready) || scriptLoader.isLoading) && (
          <FlexCenterStretched absolute>
            <Placeholder
              publicId={props.publicId}
              width={props.width}
              height={props.height}
              mediaType={MediaSymbolTypes.SPIN}
              transformation={this.props.transformation}
            />
          </FlexCenterStretched>
        )}
        {(this.state.error || failLoading) && (
          <FlexCenterStretched absolute>
            <Error width={props.width} />
          </FlexCenterStretched>
        )}
      </FlexCenterStretched>
    );
  }
}

export default withContext(Three);
